import React, { useCallback, useState } from 'react';
import { Text } from '../../../../../../ui/atoms/Text/Text';
import { Button } from '../../../../../../ui/atoms/Button/Button';
import { ConfirmDialog } from '../../../../../../ui/molecules/ConfirmDialog';
import { SUPPORT_COPY, SUPPORT_MODE } from '../../../../constants/support';
import './SupportBannerStyles.scss';
const BASE_CLASS = 'ai-support-banner';
function resolveContent(mode, showEnded, unreadCount) {
    switch (mode) {
        case SUPPORT_MODE.HANDOFF_REQUESTED:
        case SUPPORT_MODE.QUEUED:
            return {
                icon: 'fa-light fa-hourglass-half',
                title: SUPPORT_COPY.BANNER_WAITING,
                detail: SUPPORT_COPY.BANNER_WAITING_DETAIL,
                ended: false
            };
        case SUPPORT_MODE.HUMAN:
            return { icon: 'fa-light fa-headset', title: SUPPORT_COPY.BANNER_HUMAN, detail: null, ended: false };
        case SUPPORT_MODE.ENDED:
            if (!showEnded)
                return null;
            return {
                icon: 'fa-light fa-circle-check',
                title: SUPPORT_COPY.BANNER_ENDED,
                detail: unreadCount > 0 ? SUPPORT_COPY.BANNER_ENDED_UNREAD_DETAIL : SUPPORT_COPY.BANNER_ENDED_DETAIL,
                ended: true
            };
        default:
            return null;
    }
}
/**
 * Status of the live chat with InspectPoint Support, shown above the composer while a session
 * exists: Waiting for an agent… / Chatting with InspectPoint Support / Chat ended.
 *
 * "End chat and return to IPA" asks for confirmation first, and the confirmation says plainly
 * that ending the chat does NOT resolve the support request — Customer Support keeps its own
 * ticket workflow and can still follow up.
 */
export function SupportBanner({ mode, showEnded = false, unreadCount = 0, isEnding = false, notice = null, onEndChat, onDismissEnded }) {
    const [isConfirming, setIsConfirming] = useState(false);
    const content = resolveContent(mode, showEnded, unreadCount);
    const handleConfirm = useCallback(() => {
        setIsConfirming(false);
        onEndChat();
    }, [onEndChat]);
    if (!content)
        return notice ? (React.createElement(Text, { variant: "xx-small", className: `${BASE_CLASS}__notice`, role: "alert" }, notice)) : null;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: `${BASE_CLASS}${content.ended ? ` ${BASE_CLASS}--ended` : ''}`, role: "status" },
            React.createElement("i", { className: `${content.icon} ${BASE_CLASS}__icon`, "aria-hidden": "true" }),
            React.createElement("div", { className: `${BASE_CLASS}__text` },
                React.createElement(Text, { variant: "x-small" }, content.title),
                content.detail && (React.createElement(Text, { variant: "xx-small", className: `${BASE_CLASS}__detail` }, content.detail))),
            React.createElement("div", { className: `${BASE_CLASS}__actions` }, content.ended ? (onDismissEnded && (React.createElement(Button, { "aria-label": "Dismiss", onClick: onDismissEnded },
                React.createElement("i", { className: "fa-light fa-xmark", "aria-hidden": "true" })))) : (React.createElement(Button, { variant: "secondary", disabled: isEnding, onClick: () => setIsConfirming(true) }, SUPPORT_COPY.END_BUTTON)))),
        notice && (React.createElement(Text, { variant: "xx-small", className: `${BASE_CLASS}__notice`, role: "alert" }, notice)),
        React.createElement(ConfirmDialog, { isOpen: isConfirming, title: SUPPORT_COPY.END_CONFIRM_TITLE, description: SUPPORT_COPY.END_CONFIRM_BODY, confirmLabel: SUPPORT_COPY.END_CONFIRM_LABEL, cancelLabel: SUPPORT_COPY.END_CANCEL_LABEL, loading: isEnding, onConfirm: handleConfirm, onCancel: () => setIsConfirming(false) })));
}
